/**
 * ReviewDrawer — the moderation drawer for one product (PIM Wave P3 Task 3).
 * Shows the product core, a live completeness bar, and the attributes-vs-
 * requirements checklist, then gates Approve / Send-back.
 *
 * PHOTOS ARE DESCOPED for P3 (no `images` field on the admin `ProductResponse`,
 * no admin image-URL plumbing) — flagged as a follow-up in the task report.
 *
 * The completeness bar is a fresh, minimal `--g-*` progress bar rather than
 * an import of `seller-dashboard/attributes/CompletenessMeter` — that
 * component computes its OWN client-side estimate from a live schema+values
 * pair (`computeCompleteness`) and pulls the `seller-dashboard` i18n
 * namespace; it does not accept a raw server-computed percentage, so it is
 * seller-coupled, not persona-neutral. Reimplementing here avoids the first
 * cross-persona `admin/` -> `seller-dashboard/` component import.
 *
 * The send-back reason dialog is a SIBLING `BottomSheet` (toggled via
 * `actionMode`), never nested inside this drawer's own `BottomSheet` —
 * nested `BottomSheet`s cannot escape the parent's transformed panel (see
 * `attribute-fields/components/GroupManager.tsx`'s header comment). Textarea
 * bounds/behavior copy `RejectDialog.tsx` (min 5 / max 500 chars).
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { productStatusTone } from "@/features/admin/components/tone";
import { formatKgs, kgsLocaleForLanguage } from "@/lib/format-currency";
import { parseDecimal } from "@/lib/format";
import {
  useApproveProduct,
  useProductReview,
  useSendBackProduct,
} from "@/features/admin/product-review/hooks";
import type { AttributeValueOut } from "@/features/admin/product-review/api";
import type {
  FormFieldDto,
  FormSchemaResponse,
  TrilingualText,
} from "@/features/admin/category-bindings/api";

export interface ReviewDrawerProps {
  productId: string | null;
  open: boolean;
  onClose: () => void;
}

type Lang = "ru" | "ky" | "en";
type ActionMode = "none" | "sendBack";

const MIN_REASON = 5;
const MAX_REASON = 500;

function isBlank(text: string | null | undefined): boolean {
  return text === null || text === undefined || text.trim() === "";
}

/** Chosen-language text with RU fallback — mirrors the admin `FormPreview` /
 * seller `formSchema` convention (`TrilingualText.ru` is always present). */
function pickText(text: TrilingualText, lang: Lang): string {
  if (lang === "ky" && !isBlank(text.ky)) return text.ky as string;
  if (lang === "en" && !isBlank(text.en)) return text.en as string;
  return text.ru;
}

function toLang(language: string): Lang {
  const base = language.split("-")[0];
  return base === "ky" || base === "en" ? base : "ru";
}

/** Flattens every group's fields (ungrouped bucket included) into one
 * ordered list — mirrors `category-bindings/components/FormPreview.tsx`'s
 * `flattenFields`. */
function flattenFields(schema: FormSchemaResponse): FormFieldDto[] {
  return schema.groups.flatMap((section) => section.fields);
}

type CheckMark = "check" | "cross" | "dash";

/**
 * Per-field ✓/✗/— (mirrors the backend exactly): a field slug in
 * `missing_required` is ✗ (unmet VISIBLE required binding — only meaningful
 * for required fields); else a matching non-null entry in `attributes` is ✓;
 * else — (not provided, non-required — e.g. recommended/optional untouched,
 * or hidden by a visibility rule).
 */
function markFor(
  fieldSlug: string,
  missingRequired: ReadonlySet<string>,
  attributesBySlug: ReadonlyMap<string, AttributeValueOut>,
): CheckMark {
  if (missingRequired.has(fieldSlug)) return "cross";
  const attr = attributesBySlug.get(fieldSlug);
  if (attr && attr.value !== null) return "check";
  return "dash";
}

function formatValue(attr: AttributeValueOut): string {
  const raw = String(attr.value);
  return attr.unit ? `${raw} ${attr.unit}` : raw;
}

interface RequiredMissingField {
  field_slug: string;
  code: string;
  message?: string | null;
}

export function ReviewDrawer({
  productId,
  open,
  onClose,
}: ReviewDrawerProps): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const lang = toLang(i18n.language);
  const locale = kgsLocaleForLanguage(i18n.language);

  const { data: review, isLoading } = useProductReview(productId);
  const approve = useApproveProduct();
  const sendBack = useSendBackProduct();

  const [actionMode, setActionMode] = useState<ActionMode>("none");
  const [reason, setReason] = useState("");
  const [approveBlocked, setApproveBlocked] = useState<
    RequiredMissingField[] | null
  >(null);

  useEffect(() => {
    if (!open) {
      setActionMode("none");
      setReason("");
      setApproveBlocked(null);
    }
  }, [open]);

  const priceLabel = (raw: string | null): string =>
    raw == null ? "—" : formatKgs(parseDecimal(raw), locale);

  const fields = review ? flattenFields(review.form_schema) : [];
  const attributesBySlug = new Map<string, AttributeValueOut>(
    (review?.attributes ?? []).map((a) => [a.field_slug, a]),
  );
  const missingRequired = new Set(review?.missing_required ?? []);

  const fieldLabel = (fieldSlug: string): string => {
    const field = fields.find((f) => f.field_slug === fieldSlug);
    return field ? pickText(field.label, lang) : fieldSlug;
  };

  const handleApprove = async (): Promise<void> => {
    if (!productId) return;
    setApproveBlocked(null);
    try {
      await approve.mutateAsync(productId);
      toast.success(t("pim.review.toast.approved"));
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem?.code === "pim.required_missing") {
        const problemFields = (err.problem as Record<string, unknown>).fields;
        setApproveBlocked(
          Array.isArray(problemFields) ? (problemFields as RequiredMissingField[]) : [],
        );
        return;
      }
      toast.error(t("pim.review.toast.error"));
    }
  };

  const reasonValid = reason.length >= MIN_REASON && reason.length <= MAX_REASON;

  const handleSendBack = async (): Promise<void> => {
    if (!productId || !reasonValid) return;
    try {
      await sendBack.mutateAsync({ id: productId, reason });
      toast.success(t("pim.review.toast.sentBack"));
      setActionMode("none");
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(t("pim.review.toast.error"));
      }
    }
  };

  const title = review?.product.name ?? t("pim.review.title");

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title={title} desktop={isDesktop}>
        {isLoading || !review ? (
          <div className="flex flex-col gap-3 py-2">
            <div className="h-4 w-2/3 animate-pulse rounded bg-[var(--g-elevated)]" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-[var(--g-elevated)]" />
            <div className="h-24 w-full animate-pulse rounded bg-[var(--g-elevated)]" />
          </div>
        ) : (
          <div data-testid="review-drawer-body" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <GPill tone={productStatusTone(review.product.status)} size="sm">
                {t(`products.status.${review.product.status}`, {
                  defaultValue: review.product.status,
                })}
              </GPill>
            </div>

            <dl className="divide-y divide-[var(--g-hairline)]">
              <div className="flex flex-col gap-0.5 py-1.5">
                <dt className="text-xs font-medium text-[var(--g-secondary)]">
                  {t("products.detail.seller")}
                </dt>
                <dd className="text-sm">{review.product.seller_id.slice(0, 8)}</dd>
              </div>
              <div className="flex flex-col gap-0.5 py-1.5">
                <dt className="text-xs font-medium text-[var(--g-secondary)]">
                  {t("products.columns.category")}
                </dt>
                <dd className="text-sm">{pickText(review.form_schema.category_name, lang)}</dd>
              </div>
              <div className="flex flex-col gap-0.5 py-1.5">
                <dt className="text-xs font-medium text-[var(--g-secondary)]">
                  {t("products.detail.price")}
                </dt>
                <dd className="text-sm">{priceLabel(review.product.price_kgs)}</dd>
              </div>
            </dl>

            {/* Completeness — simple `--g-*` bar (see file header for the
                reuse-vs-reimplement call on `CompletenessMeter`). */}
            <div
              data-testid="review-completeness"
              className="flex flex-col gap-2 rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">
                  {t("pim.review.drawer.completeness")}
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {review.completeness}%
                </span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={review.completeness}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t("pim.review.drawer.completeness")}
                className="h-2 w-full overflow-hidden rounded-full bg-[var(--g-elevated)]"
              >
                <div
                  className="h-full rounded-full bg-[var(--g-primary)] transition-[width]"
                  style={{ width: `${review.completeness}%` }}
                />
              </div>
            </div>

            {/* Attributes-vs-requirements checklist. */}
            <section aria-label={t("pim.review.drawer.requirements")}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("pim.review.drawer.requirements")}
              </h3>
              <ul className="flex flex-col divide-y divide-[var(--g-hairline)]">
                {fields.map((field) => {
                  const mark = markFor(field.field_slug, missingRequired, attributesBySlug);
                  const attr = attributesBySlug.get(field.field_slug);
                  const toneClass =
                    mark === "cross"
                      ? "text-[var(--g-error)]"
                      : mark === "check"
                        ? "text-[var(--g-success)]"
                        : "text-[var(--g-secondary)]";
                  const symbol = mark === "cross" ? "✗" : mark === "check" ? "✓" : "—";
                  return (
                    <li
                      key={field.field_slug}
                      data-testid={`review-field-${field.field_slug}`}
                      className="flex items-center justify-between gap-3 py-2 text-sm"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{pickText(field.label, lang)}</span>
                        <span className="text-xs text-[var(--g-secondary)]">
                          {t(`pim.review.drawer.${field.requirement}`, {
                            defaultValue: field.requirement,
                          })}
                        </span>
                      </div>
                      <div className={["flex shrink-0 items-center gap-1.5", toneClass].join(" ")}>
                        <span aria-hidden="true">{symbol}</span>
                        <span className="text-xs">
                          {mark === "check" && attr
                            ? formatValue(attr)
                            : mark === "cross"
                              ? t("pim.review.drawer.missing")
                              : t("pim.review.drawer.provided")}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            {approveBlocked ? (
              <div
                data-testid="review-approve-blocked"
                role="alert"
                className="rounded-[var(--g-radius)] border border-[var(--g-error)]/40 bg-[var(--g-error)]/10 p-3 text-sm text-[var(--g-error)]"
              >
                <p className="font-medium">{t("pim.review.drawer.approveBlockedTitle")}</p>
                {approveBlocked.length > 0 ? (
                  <ul className="mt-1 list-disc pl-5">
                    {approveBlocked.map((f) => (
                      <li key={f.field_slug}>{fieldLabel(f.field_slug)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                size="md"
                data-testid="review-send-back-btn"
                onClick={() => setActionMode("sendBack")}
                className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10"
              >
                {t("pim.review.drawer.sendBack")}
              </Button>
              <Button
                variant="primary"
                size="md"
                data-testid="review-approve-btn"
                loading={approve.isPending}
                onClick={() => void handleApprove()}
                className="min-h-[44px]"
              >
                {t("pim.review.drawer.approve")}
              </Button>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Send-back reason dialog — SIBLING BottomSheet, never nested. */}
      <BottomSheet
        open={actionMode === "sendBack"}
        onClose={() => setActionMode("none")}
        title={t("pim.review.sendBack.title")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="send-back-reason" className="text-sm font-medium">
              {t("pim.review.sendBack.label")}
            </label>
            <textarea
              id="send-back-reason"
              data-testid="send-back-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("pim.review.sendBack.placeholder")}
              rows={4}
              maxLength={MAX_REASON}
              className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
              aria-describedby="send-back-reason-hint"
            />
            <p id="send-back-reason-hint" className="text-xs text-[var(--g-secondary)]">
              {t("pim.review.sendBack.hint")} ({reason.length}/{MAX_REASON})
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActionMode("none")}
              disabled={sendBack.isPending}
              className="min-h-[44px]"
            >
              {t("pim.review.sendBack.cancel")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              data-testid="send-back-confirm-btn"
              onClick={() => void handleSendBack()}
              disabled={!reasonValid || sendBack.isPending}
              className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10 disabled:opacity-50"
            >
              {t("pim.review.sendBack.submit")}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}

export default ReviewDrawer;
